/**
 * Snapshot del `location.search` INICIAL (deep-link).
 *
 * Problema: al cargar la app por una URL profunda con search params
 * (`/purchases?po=…`, `/stock?product=…`, `/lots?product=…`), TanStack Router
 * hace su primer "commit" de navegación de forma diferida (microtask `flush` de
 * `@tanstack/history`) y, durante ese arranque, la URL puede reescribirse
 * perdiendo los search params antes de que la ruta de destino llegue a leerlos.
 * El resultado: el deep-link se "pierde" en el boot y la vista abre sin su estado
 * compartible.
 *
 * Solución: capturamos los search params del **primer** `location.search` de
 * forma SÍNCRONA en el import de este módulo (antes de que el router monte y haga
 * su flush). Las rutas usan este snapshot para sembrar su estado y, en el primer
 * montaje, reescriben los params a la URL (con `replace`) si el boot los perdió.
 * El snapshot se consume UNA sola vez por clave: tras leerla se borra, de modo que
 * navegaciones internas posteriores nunca reviven un valor viejo (idempotencia).
 */

const initialParams = new URLSearchParams(
  typeof globalThis !== "undefined" && globalThis.location ? globalThis.location.search : "",
);

/** Ruta (pathname) en la que se cargó la app, para no aplicar el snapshot fuera de ella. */
const initialPath =
  typeof globalThis !== "undefined" && globalThis.location ? globalThis.location.pathname : "";

/**
 * Devuelve (y CONSUME) el valor inicial de un search param del deep-link de
 * arranque. Solo lo devuelve si la app se cargó en `pathPrefix` (p.ej.
 * `/purchases`, `/stock`, `/lots`), para que cada ruta solo restaure lo suyo.
 * Tras leerlo, lo elimina del snapshot: una segunda lectura (o una navegación
 * interna) devuelve `null`.
 */
export function takeInitialSearchParam(key: string, pathPrefix: string): string | null {
  if (!initialPath.startsWith(pathPrefix)) {
    return null;
  }
  const value = initialParams.get(key);
  if (value === null) {
    return null;
  }
  initialParams.delete(key);
  return value;
}
